import React, { useEffect } from "react";
import Icon from "../../ui/Icon";
import { useFetchTranslationStats } from "../../../hooks/translation/useFetchTranslationStats";
import Spinner from "../../ui/Spinner";
import ErrorUtils from "../../../utils/ApiUtils/ErrorUtils";
import TranslationStatsHeader from "./TranslationStatsHeader";
import TranslationStatsDetails from "./TranslationStatsDetails";
import { ALLOWED_REVIEW_TRANSLATION_STATUS } from "../../../utils/ApiUtils/TranslationHelper";
import KeyboardShortcuts from "../../KeyBoardShortCuts";

// used to display either translations details or open the shortcuts
type ActivePanel = "details" | "shortcuts" | null;

export type LabelFilter = "total" | "completed" | "pending" | "translated" | "needsReview";

interface TranslationStatsProps {
  translationId: number;
  activeFilter?: LabelFilter;
  onFilterChange: (filter: LabelFilter) => void;
  onSendForReview: () => void;
  onClose: () => void;
  onStatsRefetchReady: (refetchFn: () => void) => void;
}

const TranslationStats: React.FC<TranslationStatsProps> = ({
  translationId,
  activeFilter = "pending",
  onFilterChange,
  onSendForReview,
  onClose,
  onStatsRefetchReady,
}) => {
  // state to open  details or shortcuts
  const [activePanel, setActivePanel] = React.useState<ActivePanel>(null);

  const { data: stats, isLoading, isError, error, refetch } = useFetchTranslationStats(translationId);
  const allowReview =
    (stats?.translationStatus && !ALLOWED_REVIEW_TRANSLATION_STATUS.includes(stats?.translationStatus)) || false;

  // When header expand/collapse is triggered
  const handleToggleExpand = () => {
    setActivePanel((prev) => (prev === "details" ? null : "details"));
  };
  // When shortcuts button is clicked
  const handleOpenKeyboardShortcuts = () => {
    setActivePanel("shortcuts");
  };
  // When shortcuts modal is closed
  const handleCloseKeyboardShortcuts = () => {
    setActivePanel(null);
  };

  useEffect(() => {
    if (onStatsRefetchReady) {
      onStatsRefetchReady(refetch);
    }
  }, [refetch, onStatsRefetchReady]);

  const handleFilterClick = (filter: LabelFilter) => {
    onFilterChange?.(filter);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 items-center justify-center py-8 bg-white border-b border-gray-200">
        <span className="ml-3 text-gray-600">Loading statistics...</span>
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-8 bg-red-50 border-b border-red-200">
        <Icon icon="warn" />
        <span className="text-red-600">Failed to load stats: {ErrorUtils.getErrorMessage(error)}</span>
        <button onClick={() => refetch()} className="ml-3 px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200">
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-8 bg-gray-50 border-b border-gray-200">
        <Icon icon="database" />
        <span className="text-gray-500">No statistics available</span>
      </div>
    );
  }

  return (
    <div className="relative w-full bg-white">
      <TranslationStatsHeader
        totalItems={stats.totalItems}
        pendingItems={stats.pendingItems}
        translatedItems={stats.translatedItems}
        initialLabelsToTranslate={stats.initialLabelsToTranslate}
        needsReviewItems={stats.needsReviewItems}
        review={allowReview}
        activeFilter={activeFilter}
        onFilterChange={handleFilterClick}
        onSendForReview={onSendForReview}
        onClose={onClose}
        isExpanded={activePanel === "details"}
        onToggleExpand={handleToggleExpand}
        onToggleShortCuts={handleOpenKeyboardShortcuts}
      />

      {activePanel === "details" && (
        <TranslationStatsDetails
          totalItems={stats.totalItems}
          pendingItems={stats.pendingItems}
          translatedItems={stats.translatedItems}
          initialLabelsToTranslate={stats.initialLabelsToTranslate}
          needsReviewItems={stats.needsReviewItems}
          propertiesLabelCount={stats.propertiesLabelCount}
          i15DLabelCount={stats.i15DLabelCount}
          helpTextLabelCount={stats.helpTextLabelCount}
        />
      )}
      {activePanel === "shortcuts" && <KeyboardShortcuts isPopup={true} onClose={handleCloseKeyboardShortcuts} />}
    </div>
  );
};

export default TranslationStats;
